import {
  CartStore,
  CartItem,
  DiscountType,
  CartLevelDiscount,
  CartProductSnapshot,
  CartAddItemPayload,
  CartApiItem,
  ProductClassSeed,
} from "@/cart/types/cart";
import * as cartContracts from "@nectarpay/api-contracts/cart";
import { defineStore } from "pinia";
import router from "@/app/router";
import axios from "@/app/plugins/axios";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/app/plugins/firebase";
import { useAccountStore } from "@/account/stores/account";
import { useProductsStore } from "@/catalog/stores/products";
import {
  allocateFixedCartDiscounts,
  computeDiscountAmount,
  getCartDiscountRatio,
  getItemNetSubtotal,
  getItemOriginalSubtotal,
  getItemNetUnitAmount,
  hasDiscountApplied,
  sanitizeFixedAmount,
  sanitizePercentage,
} from "@/shared/utils/discount.utils";
import { useLocationStore } from "@/terminals/stores/locations";

const CART_LINE_ITEM_SEPARATOR = "::";

const getPriceIdFromCartItem = (item: { id?: string; priceId?: string }): string => {
  const priceId = item?.priceId;
  if (typeof priceId === "string" && priceId) {
    return priceId;
  }
  const id = item?.id;
  if (typeof id === "string" && id) {
    return id.split(CART_LINE_ITEM_SEPARATOR)[0] ?? id;
  }
  return String(id ?? "");
};

const buildLineItemId = (priceId: string, locationId?: string | null): string => {
  if (locationId) {
    return `${priceId}${CART_LINE_ITEM_SEPARATOR}${locationId}`;
  }
  return priceId;
};

const buildLocationProductListenerKey = (productId: string, locationId: string): string => {
  return `${locationId}${CART_LINE_ITEM_SEPARATOR}${productId}`;
};

const resolveCartLocationId = (): string | null => {
  const account = useAccountStore().account;
  const locationBasedInventoryEnabled =
    account?.accountSettings?.inventory?.location_based_inventory_enabled === true;

  if (!locationBasedInventoryEnabled) {
    return null;
  }

  const productStore = useProductsStore();
  const condition = productStore.filterProductCondition;

  if (condition && "locationId" in condition) {
    const rawLocationId = condition.locationId;
    return typeof rawLocationId === "string" && rawLocationId ? rawLocationId : null;
  }

  const locationStore = useLocationStore();
  const selectedLocationId = locationStore.selectedLocation?.id ?? null;
  return typeof selectedLocationId === "string" && selectedLocationId ? selectedLocationId : null;
};

const resolveProductClassId = (product: CartProductSnapshot): string => {
  const rawProductClass =
    product?.product_class ?? product?.productClass ?? product?.productClassId ?? "";

  if (typeof rawProductClass === "string") {
    return rawProductClass;
  }

  if (rawProductClass && typeof rawProductClass === "object") {
    return String(rawProductClass.id ?? "");
  }

  return "";
};

const resolveProductClassSeed = (
  product: CartProductSnapshot,
  productClassId: string,
): ProductClassSeed | null => {
  if (!productClassId) {
    return null;
  }

  const rawProductClass = product?.product_class ?? product?.productClass;
  if (!rawProductClass || typeof rawProductClass !== "object") {
    return null;
  }

  return {
    ...rawProductClass,
    id: productClassId,
    name: rawProductClass.name ?? "",
    images: Array.isArray(rawProductClass.images) ? rawProductClass.images : [],
  };
};

type CartItemDisplaySnapshot = {
  productName: string;
  productImages: string[];
  productClassName: string;
  productClassImages: string[];
};

const resolveImageList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (entry): entry is string => typeof entry === "string" && entry.trim().length > 0,
  );
};

const buildCartItemDisplaySnapshot = (
  product?: CartProductSnapshot | null,
  fallback: Partial<CartItem> = {},
): CartItemDisplaySnapshot => {
  const productClassSeed =
    product && typeof product === "object"
      ? resolveProductClassSeed(product, resolveProductClassId(product))
      : null;

  return {
    productName: (typeof product?.name === "string" && product.name) || fallback.productName || "",
    productImages:
      resolveImageList(product?.images).length > 0
        ? resolveImageList(product?.images)
        : resolveImageList(fallback.productImages),
    productClassName:
      (typeof productClassSeed?.name === "string" && productClassSeed.name) ||
      fallback.productClassName ||
      "",
    productClassImages:
      resolveImageList(productClassSeed?.images).length > 0
        ? resolveImageList(productClassSeed?.images)
        : resolveImageList(fallback.productClassImages),
  };
};

const normalizeCartItemDisplaySnapshot = (raw: CartApiItem): CartItemDisplaySnapshot => {
  return {
    productName:
      (typeof raw?.productInfo?.name === "string" && raw.productInfo.name) ||
      raw?.productName ||
      "",
    productImages:
      resolveImageList(raw?.productInfo?.images).length > 0
        ? resolveImageList(raw.productInfo?.images)
        : resolveImageList(raw?.productImages),
    productClassName:
      (typeof raw?.productClassInfo?.name === "string" && raw.productClassInfo.name) ||
      raw?.productClassName ||
      "",
    productClassImages:
      resolveImageList(raw?.productClassInfo?.images).length > 0
        ? resolveImageList(raw.productClassInfo?.images)
        : resolveImageList(raw?.productClassImages),
  };
};

export const useCartStore = defineStore("cart", {
  state: (): CartStore => ({
    loading: false,
    isVisible: false,
    items: [] as CartItem[],
    isAnimating: false,
    priceListeners: new Map(), // Map to store Firebase listeners by price ID
    productListeners: new Map(), // Map to store Firebase listeners by product ID
    locationProductListeners: new Map(), // Map to store Firebase listeners by location product doc (locationId::productId)
    productClassListeners: new Map(), // Map to store Firebase listeners by product class ID
    productClasses: new Map(), // Map to store product class data by product class ID
    products: new Map(), // Map to store product data by product ID
    cartDiscount: null,
  }),

  getters: {
    total(): number {
      return this.items.reduce((sum: number, item: CartItem) => sum + getItemNetSubtotal(item), 0);
    },

    preDiscountSubtotal(): number {
      return this.items.reduce(
        (sum: number, item: CartItem) => sum + getItemOriginalSubtotal(item),
        0,
      );
    },

    discountTotal(): number {
      return Math.max(0, this.preDiscountSubtotal - this.total);
    },

    hasAnyDiscounts(): boolean {
      return this.items.some((item: CartItem) => hasDiscountApplied(item));
    },

    // Getter to access product name by product ID
    getProductName(): (productId: string) => string | undefined {
      return (productId: string) => {
        return this.products.get(productId)?.name;
      };
    },

    getProductClassName(): (productClassId: string) => string | undefined {
      return (productClassId: string) => {
        return this.productClasses.get(productClassId)?.name;
      };
    },
  },

  actions: {
    seedCartContext(productId: string, product: CartProductSnapshot) {
      if (!productId || !product || typeof product !== "object") {
        return;
      }

      const existingProduct = this.products.get(productId) ?? {};
      this.products.set(productId, {
        ...existingProduct,
        ...product,
        id: productId,
        name: product?.name ?? existingProduct.name,
        images: Array.isArray(product?.images) ? product.images : (existingProduct.images ?? []),
      });

      const productClassId = resolveProductClassId(product);
      const productClassSeed = resolveProductClassSeed(product, productClassId);
      if (!productClassId || !productClassSeed) {
        return;
      }

      const existingProductClass = this.productClasses.get(productClassId) ?? {};
      this.productClasses.set(productClassId, {
        ...existingProductClass,
        ...productClassSeed,
        id: productClassId,
        name: productClassSeed.name ?? existingProductClass.name,
        images: Array.isArray(productClassSeed.images)
          ? productClassSeed.images
          : (existingProductClass.images ?? []),
      });
    },

    // Set up listener for a price item in the cart
    setupPriceListener(priceId: string) {
      const accountId = useAccountStore().account?.id;
      if (!accountId || this.priceListeners.has(priceId)) return;

      const priceDocRef = doc(db, "accounts", accountId, "prices", priceId);

      const unsubscribe = onSnapshot(
        priceDocRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const priceData = docSnapshot.data() as CartProductSnapshot;

            // Find all cart items with this price ID and update PRICE-SPECIFIC properties only
            this.items.forEach((item, index) => {
              if (getPriceIdFromCartItem(item) === priceId) {
                // Only update price-related info (nickname, unit_amount, currency, etc.)
                this.items[index] = {
                  ...item,
                  priceName: priceData.nickname ?? item.priceName,
                  unitAmount: priceData.unit_amount ?? item.unitAmount,
                  currency: priceData.currency ?? item.currency,
                  sku: priceData.sku ?? item.sku,
                };
              }
            });

            // Set up a product listener if we have a product ID from the price data
            if (priceData.product && !this.productListeners.has(priceData.product)) {
              this.setupProductListener(priceData.product);
            }
          }
        },
        (error) => {
          console.error(`Error listening to price ${priceId}:`, error);
        },
      );

      // Store the unsubscribe function
      this.priceListeners.set(priceId, unsubscribe);
    },

    // Set up listener for a product and store in products map
    setupProductListener(productId: string) {
      const accountId = useAccountStore().account?.id;
      if (!accountId || this.productListeners.has(productId)) return;

      const productDocRef = doc(db, "accounts", accountId, "products", productId);

      const unsubscribe = onSnapshot(
        productDocRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            // Store the product data in the map
            const productData = docSnapshot.data() as CartProductSnapshot;
            this.products.set(productId, productData);

            const productClassId = resolveProductClassId(productData);

            // Update inventory-related properties for all cart items with this product ID
            this.items.forEach((item, index) => {
              if (item.productId === productId) {
                const displaySnapshot = buildCartItemDisplaySnapshot(productData, {
                  productName: item.productName,
                  productImages: item.productImages?.length ? item.productImages : undefined,
                  productClassName:
                    item.productClassName || this.productClasses.get(productClassId)?.name,
                  productClassImages: item.productClassImages?.length
                    ? item.productClassImages
                    : this.productClasses.get(productClassId)?.images,
                });

                const next: CartItem = {
                  ...item,
                  ...displaySnapshot,
                  hasInventory:
                    productData.hasInventory !== undefined
                      ? productData.hasInventory
                      : (item.hasInventory ?? false),
                };

                // Only update stock-related properties from the global product document
                // when the cart line item is not tied to a specific location.
                if (!item.locationId) {
                  next.stockCount =
                    productData.stockCount !== undefined ? productData.stockCount : item.stockCount;
                  next.checkoutCount = productData.checkoutCount || 0;
                  next.lowStockThreshold = productData.lowStockThreshold || 5;
                  next.canOversell =
                    productData.canOversell !== undefined
                      ? productData.canOversell === true
                      : item.canOversell === true;
                  next.inventoryLowerBound =
                    productData.inventoryLowerBound !== undefined
                      ? productData.inventoryLowerBound
                      : item.inventoryLowerBound === null
                        ? null
                        : (item.inventoryLowerBound ?? 0);
                  next.isLowStock = this.isLowStock(
                    productData.stockCount,
                    productData.lowStockThreshold,
                  );
                }

                this.items[index] = next;
              }
            });
          }
        },
        (error) => {
          console.error(`Error listening to product ${productId}:`, error);
        },
      );

      // Store the unsubscribe function
      this.productListeners.set(productId, unsubscribe);
    },

    setupLocationProductListener(productId: string, locationId: string) {
      const accountId = useAccountStore().account?.id;
      if (!accountId || !locationId) return;

      const key = buildLocationProductListenerKey(productId, locationId);
      if (this.locationProductListeners.has(key)) return;

      const locationProductDocRef = doc(
        db,
        "accounts",
        accountId,
        "locations",
        locationId,
        "products",
        productId,
      );

      const unsubscribe = onSnapshot(
        locationProductDocRef,
        (docSnapshot) => {
          if (!docSnapshot.exists()) {
            return;
          }

          const productData = docSnapshot.data() as CartProductSnapshot;
          this.items.forEach((item, index) => {
            if (item.productId !== productId || item.locationId !== locationId) {
              return;
            }

            this.items[index] = {
              ...item,
              stockCount:
                productData.stockCount !== undefined ? productData.stockCount : item.stockCount,
              checkoutCount:
                productData.checkoutCount !== undefined
                  ? productData.checkoutCount
                  : (item.checkoutCount ?? 0),
              lowStockThreshold:
                productData.lowStockThreshold !== undefined
                  ? productData.lowStockThreshold
                  : (item.lowStockThreshold ?? 5),
              canOversell:
                productData.canOversell !== undefined
                  ? productData.canOversell === true
                  : item.canOversell === true,
              inventoryLowerBound:
                productData.inventoryLowerBound !== undefined
                  ? productData.inventoryLowerBound
                  : item.inventoryLowerBound === null
                    ? null
                    : (item.inventoryLowerBound ?? 0),
              isLowStock: this.isLowStock(productData.stockCount, productData.lowStockThreshold),
              hasInventory:
                productData.hasInventory !== undefined
                  ? productData.hasInventory
                  : (item.hasInventory ?? false),
            };
          });
        },
        (error) => {
          console.error(`Error listening to location product ${locationId}/${productId}:`, error);
        },
      );

      this.locationProductListeners.set(key, unsubscribe);
    },

    setupProductClassListener(productClassId: string) {
      const accountId = useAccountStore().account?.id;
      if (!accountId || this.productClassListeners.has(productClassId)) return;

      const productClassDocRef = doc(db, "accounts", accountId, "productClasses", productClassId);

      const unsubscribe = onSnapshot(
        productClassDocRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const productClassData = docSnapshot.data() as ProductClassSeed;
            this.productClasses.set(productClassId, productClassData);

            this.items.forEach((item, index) => {
              if (item.productClassId !== productClassId) {
                return;
              }

              this.items[index] = {
                ...item,
                productClassName:
                  typeof productClassData?.name === "string"
                    ? productClassData.name
                    : (item.productClassName ?? ""),
                productClassImages:
                  resolveImageList(productClassData?.images).length > 0
                    ? resolveImageList(productClassData.images)
                    : (item.productClassImages ?? []),
              };
            });
          }
        },
        (error) => {
          console.error(`Error listening to product class ${productClassId}:`, error);
        },
      );

      this.productClassListeners.set(productClassId, unsubscribe);
    },

    // Helper function to check if an item is low on stock
    isLowStock(stockCount: number | null | undefined, threshold: number | null | undefined) {
      const count = Number(stockCount);
      const thresholdNum = Number(threshold);

      return count > 0 && thresholdNum > 0 && count < thresholdNum;
    },

    // Remove a price listener
    removePriceListener(priceId: string) {
      if (this.priceListeners.has(priceId)) {
        const unsubscribe = this.priceListeners.get(priceId);
        if (unsubscribe) {
          unsubscribe();
        }
        this.priceListeners.delete(priceId);
      }
    },

    // Remove a product listener
    removeProductListener(productId: string) {
      if (this.productListeners.has(productId)) {
        const unsubscribe = this.productListeners.get(productId);
        if (unsubscribe) {
          unsubscribe();
        }
        this.productListeners.delete(productId);

        // Also remove from products map
        this.products.delete(productId);
      }
    },

    removeLocationProductListener(productId: string, locationId: string) {
      const key = buildLocationProductListenerKey(productId, locationId);
      if (!this.locationProductListeners.has(key)) {
        return;
      }

      const unsubscribe = this.locationProductListeners.get(key);
      if (unsubscribe) {
        unsubscribe();
      }
      this.locationProductListeners.delete(key);
    },

    removeProductClassListener(productClassId: string) {
      if (this.productClassListeners.has(productClassId)) {
        const unsubscribe = this.productClassListeners.get(productClassId);
        if (unsubscribe) {
          unsubscribe();
        }
        this.productClassListeners.delete(productClassId);
        this.productClasses.delete(productClassId);
      }
    },

    // Check if any cart items are still using a product ID
    isProductStillInUse(productId: string) {
      return this.items.some((item) => item.productId === productId);
    },

    // Clean up all listeners when component unmounts or is no longer needed
    cleanupAllListeners() {
      // Clean up price listeners
      for (const [, unsubscribe] of this.priceListeners.entries()) {
        if (unsubscribe) {
          unsubscribe();
        }
      }
      this.priceListeners.clear();

      // Clean up product listeners
      for (const [, unsubscribe] of this.productListeners.entries()) {
        if (unsubscribe) {
          unsubscribe();
        }
      }
      this.productListeners.clear();

      // Clean up location product listeners
      for (const [, unsubscribe] of this.locationProductListeners.entries()) {
        if (unsubscribe) {
          unsubscribe();
        }
      }
      this.locationProductListeners.clear();

      // For now, Don't clear product map to preserve product data
      // this.products.clear();

      // Clean up product class listeners
      for (const [, unsubscribe] of this.productClassListeners.entries()) {
        if (unsubscribe) {
          unsubscribe();
        }
      }
      this.productClassListeners.clear();

      // For now, Don't clear productClasses map to preserve the data
      // this.productClasses.clear();
    },

    triggerAnimation() {
      this.isAnimating = true;
      setTimeout(() => {
        this.isAnimating = false;
      }, 200);
    },

    clearAllDiscounts() {
      this.cartDiscount = null;
      this.items.forEach((item: CartItem) => {
        clearDiscountMetadata(item);
      });
    },

    applyItemDiscount(itemId: string, payload: { type: DiscountType; value: number }) {
      const item = this.items.find((entry: CartItem) => entry.id === itemId);
      if (!item) {
        return;
      }

      const sanitizedValue =
        payload.type === "percentage"
          ? sanitizePercentage(payload.value)
          : sanitizeFixedAmount(payload.value, item.unitAmount);

      const discountAmount = computeDiscountAmount(item.unitAmount, payload.type, sanitizedValue);

      if (discountAmount <= 0) {
        clearDiscountMetadata(item);
      } else {
        item.discountType = payload.type;
        item.discountTypeValue = sanitizedValue;
        item.discountAmount = discountAmount;
      }

      this.cartDiscount = null;
    },

    clearItemDiscount(itemId: string) {
      const item = this.items.find((entry: CartItem) => entry.id === itemId);
      if (!item) {
        return;
      }
      clearDiscountMetadata(item);
      this.cartDiscount = null;
    },

    applyCartDiscount(payload: CartLevelDiscount, _options?: { reapply?: boolean }): boolean {
      if (!this.items.length) {
        return false;
      }

      const subtotal = this.preDiscountSubtotal;
      if (subtotal <= 0) {
        this.clearAllDiscounts();
        return false;
      }

      const sanitizedPayload: CartLevelDiscount = {
        type: payload.type,
        value:
          payload.type === "percentage"
            ? sanitizePercentage(payload.value)
            : sanitizeFixedAmount(payload.value, subtotal),
      };

      if (sanitizedPayload.value <= 0) {
        this.clearAllDiscounts();
        return false;
      }

      const ratio = getCartDiscountRatio(subtotal, sanitizedPayload.type, sanitizedPayload.value);

      if (ratio <= 0) {
        this.clearAllDiscounts();
        return false;
      }

      this.cartDiscount = { ...sanitizedPayload };

      let fixedAllocation: number[] = [];
      let undistributed = 0;

      if (sanitizedPayload.type === "fixed") {
        const allocation = allocateFixedCartDiscounts(this.items, sanitizedPayload.value);
        fixedAllocation = allocation.perUnitDiscounts;
        undistributed = allocation.undistributed;
      }

      this.items.forEach((item: CartItem, index: number) => {
        let discountAmount = 0;
        let discountTypeValue = sanitizedPayload.value;

        if (sanitizedPayload.type === "percentage") {
          discountAmount = computeDiscountAmount(
            item.unitAmount,
            "percentage",
            sanitizedPayload.value,
          );
        } else {
          discountAmount = fixedAllocation[index] ?? 0;
          discountTypeValue = discountAmount;
        }

        if (discountAmount <= 0) {
          clearDiscountMetadata(item);
        } else {
          item.discountType = sanitizedPayload.type;
          item.discountTypeValue = discountTypeValue;
          item.discountAmount = discountAmount;
        }
      });

      if (sanitizedPayload.type === "fixed" && undistributed > 0) {
        console.warn(
          `Unable to distribute ${undistributed} cents of the cart discount due to quantity constraints.`,
        );
      }

      return true;
    },

    reapplyCartDiscount() {
      if (!this.cartDiscount) {
        return;
      }

      if (!this.items.length) {
        this.cartDiscount = null;
        return;
      }

      this.applyCartDiscount(this.cartDiscount, { reapply: true });
    },

    removeItem(itemId: string) {
      const index = this.items.findIndex((item) => item.id === itemId);
      if (index !== -1) {
        const removedItem = this.items[index];
        this.items.splice(index, 1);
        this.triggerAnimation();

        // Check if this was the last item with this price ID
        const removedPriceId = getPriceIdFromCartItem(removedItem);
        const hasSamePriceId = this.items.some(
          (item) => getPriceIdFromCartItem(item) === removedPriceId,
        );
        if (!hasSamePriceId) {
          this.removePriceListener(removedPriceId);
        }

        // Check if this was the last item with this product ID
        const hasSameProductId = this.items.some(
          (item) => item.productId === removedItem.productId,
        );
        if (!hasSameProductId) {
          this.removeProductListener(removedItem.productId);
        }

        // Check if this was the last item for this product+location
        if (removedItem.locationId) {
          const hasSameLocationProduct = this.items.some(
            (item) =>
              item.productId === removedItem.productId &&
              item.locationId === removedItem.locationId,
          );
          if (!hasSameLocationProduct) {
            this.removeLocationProductListener(removedItem.productId, removedItem.locationId);
          }
        }

        // Check if this was the last item with this product class ID
        const hasSameProductClassId = this.items.some(
          (item) => item.productClassId === removedItem.productClassId,
        );
        if (!hasSameProductClassId) {
          this.removeProductClassListener(removedItem.productClassId);
        }

        this.reapplyCartDiscount();
      }
    },

    toggleIsCartVisible() {
      this.isVisible = !this.isVisible;
    },

    getInventoryBucketQuantity(productId: string, locationId?: string | null): number {
      return this.items
        .filter(
          (item: CartItem) =>
            item.productId === productId && (item.locationId ?? null) === (locationId ?? null),
        )
        .reduce((sum, entry) => sum + entry.quantity, 0);
    },

    getInventoryLowerBound(
      item: Pick<CartItem, "canOversell" | "inventoryLowerBound">,
    ): number | null {
      if (item.canOversell !== true) {
        return 0;
      }

      if (item.inventoryLowerBound === null) {
        return null;
      }

      const lowerBound = Number(item.inventoryLowerBound);
      return Number.isFinite(lowerBound) ? lowerBound : 0;
    },

    getInventoryRemainingQuantity(item: CartItem): number | null {
      if (item.hasInventory !== true) {
        return null;
      }

      const lowerBound = this.getInventoryLowerBound(item);
      if (lowerBound === null) {
        return null;
      }

      const stockCount = typeof item.stockCount === "number" ? item.stockCount : 0;
      return (
        stockCount -
        lowerBound -
        this.getInventoryBucketQuantity(item.productId, item.locationId ?? null)
      );
    },

    isItemAtMaxStock(item: CartItem): boolean {
      const remainingQuantity = this.getInventoryRemainingQuantity(item);
      return remainingQuantity !== null && remainingQuantity <= 0;
    },

    isItemOverOversellLimit(item: CartItem): boolean {
      const remainingQuantity = this.getInventoryRemainingQuantity(item);
      return remainingQuantity !== null && remainingQuantity < 0;
    },

    isItemInOversellTerritory(item: CartItem): boolean {
      if (item.hasInventory !== true) {
        return false;
      }

      const stockCount = typeof item.stockCount === "number" ? item.stockCount : 0;
      return this.getInventoryBucketQuantity(item.productId, item.locationId ?? null) > stockCount;
    },

    getOversellLimitLabel(item: Pick<CartItem, "canOversell" | "inventoryLowerBound">): string {
      const lowerBound = this.getInventoryLowerBound(item);
      if (lowerBound === null) {
        return "∞";
      }
      return String(lowerBound);
    },

    updateItemQuantity(itemId: string, change: number): boolean {
      const item = this.items.find((item) => item.id === itemId);
      if (!item) {
        return false;
      }

      if (change < 0 && item.quantity === 1) {
        return false;
      }

      if (change > 0) {
        if (this.isItemAtMaxStock(item)) {
          return false;
        }

        item.quantity = item.quantity + change;
        this.triggerAnimation();
        this.reapplyCartDiscount();
        return true;
      }

      if (change < 0) {
        item.quantity = Math.max(1, item.quantity + change);
        this.triggerAnimation();
        this.reapplyCartDiscount();
        return true;
      }

      return false;
    },

    resetCart() {
      this.triggerAnimation();
      this.clearAllDiscounts();
      // Clean up all listeners before clearing the cart
      this.cleanupAllListeners();
      this.items = [];
    },

    async checkoutCart() {
      this.loading = true;
      try {
        const locationStore = useLocationStore();
        const locationId = locationStore.selectedLocation?.id ?? null;

        const checkoutItems = this.items.map((item) => {
          const payload: {
            priceId: string;
            quantity: number;
            locationId?: string;
            discountType?: DiscountType;
            discountTypeValue?: number;
            discountUnitAmount?: number;
          } = {
            priceId: getPriceIdFromCartItem(item),
            quantity: item.quantity,
          };

          if (item.locationId) {
            payload.locationId = item.locationId;
          }

          if ((item.discountAmount ?? 0) > 0) {
            payload.discountType = item.discountType;
            payload.discountTypeValue = item.discountTypeValue;
            payload.discountUnitAmount = getItemNetUnitAmount(item);
          }

          return payload;
        });

        const checkoutPayload = cartContracts.createCartRequestSchema.parse({
          locationId,
          items: checkoutItems,
        });
        const response = await axios.post("/cart", checkoutPayload);
        if (response.data.paymentIntentId) {
          //redirect to payment process page
          router.push({
            name: "processPayment",
            params: {
              id: response.data.paymentIntentId,
            },
          });
          this.resetCart();
        }
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
      } finally {
        this.loading = false;
      }
    },

    // New method specifically for adding a product with a specific price to the cart
    async addProductWithPrice(
      product: CartProductSnapshot,
      priceId: string,
      inventoryValidated = false,
    ): Promise<boolean> {
      const productStore = useProductsStore();

      const productId = product.id;
      if (!productId || !priceId) {
        console.error("Both productId and priceId are required");
        return false;
      }

      const accountId = useAccountStore().account?.id;
      const scannedLocationId =
        typeof product.locationId === "string" && product.locationId ? product.locationId : null;
      const locationId = scannedLocationId ?? resolveCartLocationId();
      const lineItemId = buildLineItemId(priceId, locationId);
      const displaySnapshot = buildCartItemDisplaySnapshot(product);

      // Check if the item is already in the cart by (priceId, locationId)
      const itemInCart = this.items?.find(
        (item) => item.id === lineItemId && item.productId === productId,
      );

      if (itemInCart) {
        itemInCart.productName = displaySnapshot.productName || itemInCart.productName;
        itemInCart.productImages =
          displaySnapshot.productImages.length > 0
            ? displaySnapshot.productImages
            : (itemInCart.productImages ?? []);
        itemInCart.productClassName =
          displaySnapshot.productClassName || itemInCart.productClassName;
        itemInCart.productClassImages =
          displaySnapshot.productClassImages.length > 0
            ? displaySnapshot.productClassImages
            : (itemInCart.productClassImages ?? []);

        if (product.hasInventory !== undefined) {
          itemInCart.hasInventory = product.hasInventory === true;
        }
        if (typeof product.stockCount === "number") {
          itemInCart.stockCount = product.stockCount;
        }
        if (typeof product.lowStockThreshold === "number") {
          itemInCart.lowStockThreshold = product.lowStockThreshold;
          itemInCart.isLowStock = this.isLowStock(product.stockCount, product.lowStockThreshold);
        }
        if (Object.prototype.hasOwnProperty.call(product, "canOversell")) {
          itemInCart.canOversell = product.canOversell === true;
        }
        if (Object.prototype.hasOwnProperty.call(product, "inventoryLowerBound")) {
          itemInCart.inventoryLowerBound = product.inventoryLowerBound;
        }

        if (!inventoryValidated && this.isItemAtMaxStock(itemInCart)) {
          return false;
        }

        itemInCart.quantity = itemInCart.quantity + 1;
        this.triggerAnimation();
        this.reapplyCartDiscount();
        return true;
      } else {
        this.seedCartContext(productId, product);

        // const product = this.products.get(productId);
        const productClassRef =
          product?.product_class && typeof product.product_class === "object"
            ? product.product_class
            : undefined;
        const defaultImage = await productStore.getProductDefaultImage(
          product?.images,
          productClassRef,
        );
        const existingPriceItem = this.items.find(
          (entry) => getPriceIdFromCartItem(entry) === priceId,
        );
        const productClassId = resolveProductClassId(product);

        // Create a basic cart item structure
        const cartItem: CartItem = {
          id: lineItemId,
          priceId,
          priceName:
            existingPriceItem?.priceName ??
            product?.default_price?.nickname ??
            product?.priceName ??
            "",
          productId: productId, // Reference to the product
          productClassId,
          productName: displaySnapshot.productName,
          productImages: displaySnapshot.productImages,
          productClassName: displaySnapshot.productClassName,
          productClassImages: displaySnapshot.productClassImages,
          locationId: locationId || undefined,
          quantity: 1,
          description: product?.description ?? "",
          unitAmount:
            existingPriceItem?.unitAmount ??
            product?.default_price?.unit_amount ??
            product?.unitAmount ??
            0,
          image: defaultImage || "",
          currency:
            existingPriceItem?.currency ??
            product?.default_price?.currency ??
            product?.currency ??
            "usd",
          stockCount: product?.stockCount || 0, // Default to 0 instead of null
          checkoutCount: 0,
          lowStockThreshold: product?.lowStockThreshold || 0, // Default to 0 instead of null
          canOversell: product?.canOversell === true,
          inventoryLowerBound:
            product?.inventoryLowerBound === null
              ? null
              : typeof product?.inventoryLowerBound === "number"
                ? product.inventoryLowerBound
                : 0,
          isLowStock: false,
          sku: existingPriceItem?.sku ?? product?.sku ?? "",
          hasInventory: !!product?.hasInventory,
        };

        // Add to cart first so listeners can update it
        this.items.push(cartItem);
        this.triggerAnimation();

        // Set up listeners for product and price data unless the caller supplied a complete
        // point-in-time kiosk snapshot. Kiosk sessions should keep the stock bucket returned by
        // the kiosk API instead of being overwritten by account-level product documents.
        if (accountId && product?.skipRealtimeListeners !== true) {
          this.setupProductListener(productId);
          this.setupPriceListener(priceId);
          if (locationId) {
            this.setupLocationProductListener(productId, locationId);
          }
          if (cartItem.productClassId) {
            this.setupProductClassListener(cartItem.productClassId);
          }
        }

        this.reapplyCartDiscount();
        return true;
      }
    },

    addItem(item: CartAddItemPayload) {
      // Get price information
      const priceId = item.default_price?.id || item.id;
      const priceName = item.default_price?.nickname || item.nickname || "";
      const unitAmount = item.unit_amount || item.default_price?.unit_amount || 0;
      const currency = item.currency || item.default_price?.currency || "usd";
      const sku = item.sku || item.default_price?.sku || "";

      // Get product information - whether it's from a relatedProduct or directly in the item
      const productId = item.relatedProduct?.id || item.product || item.id;

      // If we have both productId and priceId, use the dedicated method
      if (productId && priceId) {
        const locationId = resolveCartLocationId();
        const lineItemId = buildLineItemId(priceId, locationId);

        // Store initial product and product class data so names resolve immediately,
        // even when Firestore listeners are unavailable (such as kiosk-only sessions).
        if (item.relatedProduct) {
          this.seedCartContext(productId, item.relatedProduct);
        }

        const displaySnapshot = buildCartItemDisplaySnapshot(item.relatedProduct ?? item);

        // If the item already has all its data, create a complete cart item
        const cartItem: CartItem = {
          id: lineItemId,
          priceId,
          priceName,
          productId,
          productClassId: resolveProductClassId(item.relatedProduct ?? item),
          productName: displaySnapshot.productName,
          productImages: displaySnapshot.productImages,
          productClassName: displaySnapshot.productClassName,
          productClassImages: displaySnapshot.productClassImages,
          locationId: locationId || undefined,
          quantity: 1,
          description: item.relatedProduct?.description || item.description || "",
          unitAmount,
          image: item.relatedProduct?.images?.[0] || item.images?.[0] || "",
          currency,
          // Get initial stock info from product rather than price
          stockCount: item.relatedProduct?.stockCount || item.stockCount || 0,
          checkoutCount: item.relatedProduct?.checkoutCount || item.checkoutCount || 0,
          lowStockThreshold: item.relatedProduct?.lowStockThreshold || item.lowStockThreshold || 5,
          canOversell:
            item.relatedProduct?.canOversell !== undefined
              ? item.relatedProduct.canOversell === true
              : item.canOversell === true,
          inventoryLowerBound:
            item.relatedProduct?.inventoryLowerBound === null || item.inventoryLowerBound === null
              ? null
              : typeof item.relatedProduct?.inventoryLowerBound === "number"
                ? item.relatedProduct.inventoryLowerBound
                : typeof item.inventoryLowerBound === "number"
                  ? item.inventoryLowerBound
                  : 0,
          isLowStock: this.isLowStock(
            item.relatedProduct?.stockCount || item.stockCount,
            item.relatedProduct?.lowStockThreshold || item.lowStockThreshold,
          ),
          sku,
          hasInventory:
            item.relatedProduct?.hasInventory !== undefined
              ? item.relatedProduct.hasInventory
              : item.hasInventory !== undefined
                ? item.hasInventory
                : false,
        };

        // Check if the item is already in the cart by (priceId, locationId)
        const itemInCart = this.items?.find((i) => i.id === cartItem.id);

        if (itemInCart) {
          itemInCart.productName = displaySnapshot.productName || itemInCart.productName;
          itemInCart.productImages =
            displaySnapshot.productImages.length > 0
              ? displaySnapshot.productImages
              : (itemInCart.productImages ?? []);
          itemInCart.productClassName =
            displaySnapshot.productClassName || itemInCart.productClassName;
          itemInCart.productClassImages =
            displaySnapshot.productClassImages.length > 0
              ? displaySnapshot.productClassImages
              : (itemInCart.productClassImages ?? []);

          if (this.isItemAtMaxStock(itemInCart)) {
            return;
          }

          // Increment quantity for existing items
          itemInCart.quantity = itemInCart.quantity + 1;
          this.triggerAnimation();
          this.reapplyCartDiscount();
        } else {
          // Add new item to cart
          this.items.push(cartItem);
          this.triggerAnimation();

          // Set up listeners for this item
          this.setupProductListener(productId);
          this.setupPriceListener(priceId);
          if (locationId) {
            this.setupLocationProductListener(productId, locationId);
          }
          if (item.productClassId) {
            this.setupProductClassListener(item.productClassId);
          }

          this.reapplyCartDiscount();
        }
      } else {
        console.error("Missing productId or priceId", { productId, priceId, item });
      }
    },

    async getCartData(paymentIntentId: string): Promise<CartApiItem[] | undefined> {
      this.loading = true;
      try {
        const response = await axios.get<CartApiItem[]>(`/cart/${paymentIntentId}`);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
      } finally {
        this.loading = false;
      }
    },

    async copyCart(paymentIntentId: string): Promise<CartApiItem[] | undefined> {
      this.resetCart();
      this.loading = true;
      try {
        const response = await axios.get<CartApiItem[]>(`/cart/${paymentIntentId}`);
        this.items = Array.isArray(response.data)
          ? response.data.map((raw: CartApiItem): CartItem => {
              const priceId = getPriceIdFromCartItem(raw);
              const locationId =
                typeof raw?.locationId === "string" && raw.locationId ? raw.locationId : undefined;

              const id =
                typeof raw?.id === "string" && raw.id.includes(CART_LINE_ITEM_SEPARATOR)
                  ? raw.id
                  : buildLineItemId(priceId, locationId);

              return {
                ...raw,
                ...normalizeCartItemDisplaySnapshot(raw),
                id,
                priceId,
                priceName: raw.priceName ?? "",
                productId: raw.productId ?? "",
                productClassId: raw.productClassId ?? "",
                quantity: raw.quantity ?? 1,
                unitAmount: raw.unitAmount ?? 0,
                image: raw.image ?? "",
                currency: raw.currency ?? "usd",
                description: raw.description ?? "",
                locationId,
              };
            })
          : [];

        // Set up listeners for all items in the cart
        for (const item of this.items) {
          const priceId = getPriceIdFromCartItem(item);
          if (priceId && !this.priceListeners.has(priceId)) {
            this.setupPriceListener(priceId);
          }
          if (item.productId && !this.productListeners.has(item.productId)) {
            this.setupProductListener(item.productId);
          }
          if (item.productId && item.locationId) {
            this.setupLocationProductListener(item.productId, item.locationId);
          }
          if (item.productClassId) {
            this.setupProductClassListener(item.productClassId);
          }
        }

        useSnackBarStore().toastSuccess({ message: "Cart items loaded" });
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
      } finally {
        this.loading = false;
      }
    },
  },
});

const clearDiscountMetadata = (item: CartItem) => {
  item.discountType = undefined;
  item.discountTypeValue = undefined;
  item.discountAmount = undefined;
};
